/**
 * UserInstitutionSelect - Select from current user's linked institutions
 *
 * Manually fetches and formats institution data to show approval status
 */
import React, { useState, useEffect } from "react";
import { intl, fetchData } from "dmsbasereact";
import { DmsAutocomplete } from "../DmsAutoComplete/DmsAutoComplete";
import { getBaseURL } from "../../build_config";
import { InstitutionStatusEnum } from "../../constants/InstitutionStatus";

export const UserInstitutionSelect = ({
    label = null,
    value = null,
    onChange,
    required = false,
    disabled = false,
    fullWidth = true,
    error = false,
    helperText = null,
    sx = {},
    showApprovalStatus = false,
    externalOptions = null,
}) => {
    const defaultLabel = intl.get("Institution").d("Institution");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (externalOptions) return;

        const loadInstitutions = async () => {
            setLoading(true);
            try {
                const response = await fetchData(`${getBaseURL()}/institution/user-linked`, "GET", true);
                const institutions = response.data.data || [];

                // Format options with approval status if enabled
                const formattedOptions = institutions.map((inst) => {
                    let displayLabel = inst.name;

                    // Check if institution is INACTIVE (highest priority - distinct from "not approved")
                    const isInactive = inst.status === InstitutionStatusEnum.INACTIVE;
                    const isInstitutionApproved = inst.status === InstitutionStatusEnum.APPROVED;

                    if (isInactive) {
                        // Institution was deactivated - always show "Instituição Inativa"
                        displayLabel = `${inst.name} - ${intl.get("InactiveInstitution").d("Instituição Inativa")}`;
                    } else if (!isInstitutionApproved) {
                        // Institution is pending approval (not yet approved, but not inactive)
                        displayLabel = `${inst.name} - ${intl.get("InstitutionPendingApproval").d("Aguardando aprovação da instituição")}`;
                    } else if (showApprovalStatus && !inst.is_approved) {
                        // Institution is approved, but USER is not approved at this institution
                        displayLabel = `${inst.name} - ${intl.get("UserPendingApproval").d("Aguardando aprovação do usuário")}`;
                    } else if (showApprovalStatus && inst.is_approved && inst.doctor_declaration_accepted === false) {
                        // User is approved at institution, but doctor hasn't accepted declaration for THIS institution
                        // Only show if explicitly false (not null/undefined which means no approval record exists)
                        displayLabel = `${inst.name} - ${intl.get("DoctorDeclarationNotAccepted").d("Declaração de Vínculo Institucional não aceita")}`;
                    }

                    return {
                        label: displayLabel,
                        value: inst.organization_branch_pk,
                        category: inst.category,
                    };
                });

                setOptions(formattedOptions);
            } catch (error) {
                console.error("Error loading institutions:", error);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        };

        loadInstitutions();
    }, [showApprovalStatus, externalOptions]);

    const effectiveOptions = externalOptions || options;

    return (
        <DmsAutocomplete
            key={`institution-select-${effectiveOptions.length}`}
            label={label || defaultLabel}
            value={value}
            onChange={onChange}
            options={effectiveOptions}
            required={required}
            disabled={disabled || loading}
            fullWidth={fullWidth}
            loading={loading}
            error={error}
            helperText={helperText}
            sx={sx}
        />
    );
};

export default UserInstitutionSelect;
